import { type CalendarItem, DOMAIN_LABEL, DOMAIN_ORDER } from "@/lib/overview";
import { Box, Text } from "@okshaun/components";
import { css, cx } from "@styled-system/css";
import { CalendarGrid } from "./calendar-grid";
import { CalendarStrip } from "./calendar-strip";
import { pressableRaw, segmentShapeRaw } from "./pressable";

export type CalendarView = "strip" | "grid";

interface CalendarSectionProps {
  view: CalendarView;
  onView: (view: CalendarView) => void;
  days: string[];
  today: string;
  byDay: Map<string, CalendarItem[]>;
  calendarUrl: string;
  personalConnected: boolean;
  loading: boolean;
  error: string | null;
}

const seg = css(pressableRaw, segmentShapeRaw);

const swatch = css({ w: "8", h: "8", borderRadius: "2", display: "inline-block" });
const SWATCH = {
  cetec: css({ bg: "domain.cetec" }),
  mkbd: css({ bg: "domain.mkbd" }),
  personal: css({ bg: "domain.personal" }),
} as const;

export function CalendarSection({
  view,
  onView,
  days,
  today,
  byDay,
  calendarUrl,
  personalConnected,
  loading,
  error,
}: CalendarSectionProps) {
  return (
    <Box as="section" display="flex" flexDir="column" gap="12">
      <Box display="flex" alignItems="center" gap="16" flexWrap="wrap">
        <Text
          textStyle="mono.xs"
          color="text.subtlest"
          letterSpacing="widest"
          textTransform="uppercase"
        >
          Next 14 days
        </Text>
        <Box display="inline-flex" gap="2" p="2" border="default" borderRadius="4">
          <button
            type="button"
            aria-pressed={view === "strip"}
            className={seg}
            onClick={() => onView("strip")}
          >
            Strip
          </button>
          <button
            type="button"
            aria-pressed={view === "grid"}
            className={seg}
            onClick={() => onView("grid")}
          >
            Grid
          </button>
        </Box>
        <a
          href={calendarUrl}
          target="_blank"
          rel="noreferrer"
          className={css({
            fontFamily: "mono",
            fontSize: "12",
            color: "text.subtle",
            _hover: { color: "text" },
          })}
        >
          Open calendar ↗
        </a>
        <Box display="flex" gap="14" alignItems="center" ml="auto" flexWrap="wrap">
          {DOMAIN_ORDER.map((d) => (
            <Box key={d} display="inline-flex" alignItems="center" gap="6">
              <span className={cx(swatch, SWATCH[d])} />
              <Text textStyle="mono.xs" color="text.subtle">
                {DOMAIN_LABEL[d]}
              </Text>
            </Box>
          ))}
          <Box display="inline-flex" alignItems="center" gap="6">
            <span
              className={cx(swatch, css({ border: "[1px dashed]", borderColor: "border.bold" }))}
            />
            <Text textStyle="mono.xs" color="text.subtle">
              vault date
            </Text>
          </Box>
          {!personalConnected && !loading && !error && (
            <Text textStyle="mono.xs" color="text.warning">
              personal Gmail calendar: not connected
            </Text>
          )}
          {loading && (
            <Text textStyle="mono.xs" color="text.subtlest">
              loading calendar…
            </Text>
          )}
          {error && (
            <Text textStyle="mono.xs" color="text.danger">
              calendar: {error}
            </Text>
          )}
        </Box>
      </Box>
      {view === "strip" ? (
        <CalendarStrip days={days} today={today} byDay={byDay} />
      ) : (
        <CalendarGrid days={days} today={today} byDay={byDay} />
      )}
    </Box>
  );
}
